import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Store,
  RefreshCw,
  Search,
  Link,
  Copy,
  Check,
  Plus,
  Pencil,
  ChevronLeft,
  ChevronRight,
  Download,
  Trash2,
  LogIn,
  Key,
  Eye,
  EyeOff,
} from 'lucide-react';
import { toast } from 'sonner';
import api, { merchantsAPI } from '../services/api';

const MerchantsPage = () => {
  const { hasPermission, login: authLogin } = useAuth();
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0 });
  const [search, setSearch] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  
  // Create/Edit merchant dialog
  const [showMerchantDialog, setShowMerchantDialog] = useState(false);
  const [editingMerchant, setEditingMerchant] = useState(null);
  const [merchantForm, setMerchantForm] = useState({
    name: '',
    company_name: '',
    bin: '',
    telegram: '',
    cabinet_login: '',
    cabinet_password: '',
  });
  const [savingMerchant, setSavingMerchant] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Payment link dialog
  const [showCreateLink, setShowCreateLink] = useState(false);
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [createdLink, setCreatedLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [linkForm, setLinkForm] = useState({
    amount: '',
    currency: 'KZT',
    description: '',
    order_number: '',
  });

  // Credentials dialog
  const [showCredsDialog, setShowCredsDialog] = useState(false);
  const [credsMerchant, setCredsMerchant] = useState(null);
  const [merchantCreds, setMerchantCreds] = useState({ cabinet_login: '', cabinet_password: '' });
  const [showCredsPassword, setShowCredsPassword] = useState(false);
  const [savingCreds, setSavingCreds] = useState(false);

  const canWrite = hasPermission('merchants', 'write');
  const canDelete = hasPermission('merchants', 'delete');
  const canViewKeys = hasPermission('merchants', 'keys');

  const fetchMerchants = async (page = 1, searchQuery = search) => {
    try {
      setLoading(true);
      const params = { page, page_size: pagination.pageSize };
      if (searchQuery) params.search = searchQuery;
      
      const response = await merchantsAPI.list(params);
      setMerchants(response.data.merchants || []);
      setPagination({
        page: response.data.page,
        pageSize: response.data.page_size,
        total: response.data.total,
      });
    } catch (err) {
      toast.error('Ошибка загрузки мерчантов');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMerchants();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchMerchants(1, search);
  };

  // Create/Edit merchant handlers
  const openCreateMerchantDialog = () => {
    setEditingMerchant(null);
    setMerchantForm({ 
      name: '', 
      company_name: '', 
      bin: '', 
      telegram: '',
      cabinet_login: '',
      cabinet_password: '',
    });
    setShowPassword(false);
    setShowMerchantDialog(true);
  };

  const openEditMerchantDialog = async (merchant) => {
    setEditingMerchant(merchant);
    setMerchantForm({
      name: merchant.name || '',
      company_name: merchant.company_name || '',
      bin: merchant.bin || '',
      telegram: merchant.telegram || '',
      cabinet_login: merchant.cabinet_login || '',
      cabinet_password: '', // Don't show password, require re-entry
    });
    setShowPassword(false);
    setShowMerchantDialog(true);
  };

  const handleSaveMerchant = async (e) => {
    e.preventDefault();
    
    if (!merchantForm.name.trim()) {
      toast.error('Введите название мерчанта');
      return;
    }

    try {
      setSavingMerchant(true);
      const submitData = { ...merchantForm };
      // Don't send empty password on edit
      if (editingMerchant && !submitData.cabinet_password) {
        delete submitData.cabinet_password;
      }
      
      if (editingMerchant) {
        await merchantsAPI.update(editingMerchant.id, submitData);
        toast.success('Мерчант обновлен');
      } else {
        await merchantsAPI.create(submitData);
        toast.success('Мерчант создан');
      }
      setShowMerchantDialog(false);
      fetchMerchants(pagination.page);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setSavingMerchant(false);
    }
  };

  // Impersonate merchant (login as merchant)
  const handleLoginAsMerchant = async (merchant) => {
    try {
      const response = await api.post(`/auth/impersonate-merchant/${merchant.id}`);
      const { access_token, merchant_name } = response.data;
      
      // Store token and redirect to cabinet
      localStorage.setItem('impersonate_token', access_token);
      localStorage.setItem('impersonate_merchant', JSON.stringify({name: merchant_name, id: merchant.id}));
      
      // Open cabinet in new tab
      window.location.href = '/cabinet';
      toast.success(`Вход как "${merchant_name}"`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка входа');
    }
  };

  // Credentials dialog handlers
  const openCredsDialog = async (merchant) => {
    setCredsMerchant(merchant);
    setShowCredsPassword(false);
    try {
      const response = await api.get(`/merchants/${merchant.id}/credentials`);
      setMerchantCreds({
        cabinet_login: response.data.cabinet_login || '',
        cabinet_password: response.data.cabinet_password || '',
      });
    } catch (err) {
      setMerchantCreds({ cabinet_login: '', cabinet_password: '' });
    }
    setShowCredsDialog(true);
  };

  const handleSaveCreds = async (e) => {
    e.preventDefault();
    if (!credsMerchant) return;
    
    try {
      setSavingCreds(true);
      await api.put(`/merchants/${credsMerchant.id}/credentials`, merchantCreds);
      toast.success('Учётные данные сохранены');
      setShowCredsDialog(false);
      fetchMerchants(pagination.page);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setSavingCreds(false);
    }
  };

  // Payment link handlers
  const openCreateLinkDialog = (merchant) => {
    setSelectedMerchant(merchant);
    setLinkForm({
      amount: '',
      currency: 'KZT',
      description: '',
      order_number: '',
    });
    setCreatedLink(null);
    setShowCreateLink(true);
  };

  const handleCreateLink = async (e) => {
    e.preventDefault();
    
    if (!linkForm.amount || parseFloat(linkForm.amount) <= 0) {
      toast.error('Введите корректную сумму');
      return;
    }

    try {
      const response = await merchantsAPI.createPaymentLink({
        merchant_id: selectedMerchant.id,
        amount: Math.round(parseFloat(linkForm.amount) * (linkForm.currency === 'KZT' ? 1 : 100)),
        currency: linkForm.currency,
        description: linkForm.description || undefined,
        order_number: linkForm.order_number || undefined,
      });
      
      setCreatedLink(response.data);
      toast.success('Ссылка создана!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка создания ссылки');
    }
  };

  const copyToClipboard = async (text) => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Скопировано!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDeleteMerchant = async (merchantId) => {
    if (!window.confirm('Вы уверены, что хотите удалить этого мерчанта?')) return;
    
    try {
      setDeletingId(merchantId);
      await merchantsAPI.delete(merchantId);
      toast.success('Мерчант удален');
      fetchMerchants(pagination.page);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка удаления');
    } finally {
      setDeletingId(null);
    }
  };

  const formatAmount = (amount, currency) => {
    if (currency === 'KZT') {
      return `${amount.toLocaleString('ru-RU')} ₸`;
    }
    return `${(amount / 100).toFixed(2)} ${currency}`;
  };

  return (
    <div className="space-y-6" data-testid="merchants-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Мерчанты</h1>
          <p className="text-slate-400 mt-1">
            Всего: {pagination.total.toLocaleString('ru-RU')} мерчантов
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={async () => {
              try {
                const response = await api.get('/export/merchants', { responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `merchants_${new Date().toISOString().slice(0, 10)}.csv`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                toast.success('Экспорт завершён');
              } catch (err) {
                toast.error('Ошибка экспорта');
              }
            }}
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
            data-testid="export-merchants-btn"
          >
            <Download className="w-4 h-4 mr-1" />
            Экспорт
          </Button>
          {canWrite && (
            <Button
              onClick={openCreateMerchantDialog}
              className="bg-emerald-500 hover:bg-emerald-600"
              data-testid="create-merchant-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Создать
            </Button>
          )}
          <Button
            onClick={() => fetchMerchants(pagination.page)}
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            placeholder="Поиск по имени мерчанта..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-slate-700/50 border-slate-600 text-white"
            data-testid="merchant-search-input"
          />
        </div>
        <Button type="submit" className="bg-emerald-500 hover:bg-emerald-600">
          Найти
        </Button>
      </form>

      {/* Info banner */}
      <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
        <p className="text-sm text-blue-400">
          <strong>💡 Подсказка:</strong> Для настройки роутинга платежей, тестовых данных и API ключей перейдите в раздел <strong>Терминалы</strong>.
        </p>
      </div>

      {/* Merchants Table */}
      <Card className="bg-slate-800/50 border-slate-700/50">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw className="w-8 h-8 text-emerald-500 animate-spin" />
            </div>
          ) : merchants.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-slate-400">
              <Store className="w-12 h-12 mb-4 opacity-50" />
              <p>Мерчанты не найдены</p>
              {canWrite && (
                <Button
                  onClick={openCreateMerchantDialog}
                  className="mt-4 bg-emerald-500 hover:bg-emerald-600"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Создать первого мерчанта
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left p-4 text-sm font-medium text-slate-400">ID</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Название</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Компания</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Логин ЛК</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Telegram</th>
                    <th className="text-right p-4 text-sm font-medium text-slate-400">Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {merchants.map((merchant) => (
                    <tr
                      key={merchant.id}
                      className="border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors"
                      data-testid={`merchant-row-${merchant.id}`}
                    >
                      <td className="p-4">
                        <span className="text-white font-medium">#{merchant.id}</span>
                      </td>
                      <td className="p-4">
                        <span className="text-white">{merchant.name || 'N/A'}</span>
                      </td>
                      <td className="p-4">
                        <span className="text-slate-300 text-sm">{merchant.company_name || '—'}</span>
                      </td>
                      <td className="p-4">
                        {merchant.cabinet_login ? (
                          <span className="px-2 py-1 rounded text-xs font-medium bg-emerald-500/20 text-emerald-400">
                            {merchant.cabinet_login}
                          </span>
                        ) : (
                          <span className="text-slate-500 text-sm">Не настроен</span>
                        )}
                      </td>
                      <td className="p-4">
                        <span className="text-slate-300 text-sm">{merchant.telegram || '—'}</span>
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {canViewKeys && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleLoginAsMerchant(merchant)}
                              className="border-purple-500/50 text-purple-400 hover:bg-purple-500/20"
                              title="Войти как мерчант"
                              data-testid={`login-as-merchant-${merchant.id}`}
                            >
                              <LogIn className="w-4 h-4 mr-1" />
                              Войти
                            </Button>
                          )}
                          {canWrite && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openCredsDialog(merchant)}
                                className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20"
                                title="Настройки входа"
                                data-testid={`creds-merchant-${merchant.id}`}
                              >
                                <Key className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openEditMerchantDialog(merchant)}
                                className="border-slate-600 text-slate-300 hover:bg-slate-700"
                                data-testid={`edit-merchant-${merchant.id}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openCreateLinkDialog(merchant)}
                                className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/20"
                                data-testid={`create-link-${merchant.id}`}
                              >
                                <Link className="w-4 h-4 mr-1" />
                                Ссылка
                              </Button>
                            </>
                          )}
                          {canDelete && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteMerchant(merchant.id)}
                              className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                              disabled={deletingId === merchant.id}
                              data-testid={`delete-merchant-${merchant.id}`}
                            >
                              {deletingId === merchant.id ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {Math.ceil(pagination.total / pagination.pageSize) > 1 && (
            <div className="flex items-center justify-between p-4 border-t border-slate-700">
              <p className="text-sm text-slate-400">
                Страница {pagination.page} из {Math.ceil(pagination.total / pagination.pageSize)}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchMerchants(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  className="border-slate-600 text-slate-300"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchMerchants(pagination.page + 1)}
                  disabled={pagination.page >= Math.ceil(pagination.total / pagination.pageSize)}
                  className="border-slate-600 text-slate-300"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Merchant Dialog */}
      <Dialog open={showMerchantDialog} onOpenChange={setShowMerchantDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Store className="w-5 h-5 text-emerald-500" />
              {editingMerchant ? 'Редактировать мерчанта' : 'Новый мерчант'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {editingMerchant ? `Редактирование мерчанта #${editingMerchant.id}` : 'Заполните данные нового мерчанта'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSaveMerchant} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Название *</Label>
              <Input
                value={merchantForm.name}
                onChange={(e) => setMerchantForm({ ...merchantForm, name: e.target.value })}
                placeholder="Название мерчанта"
                className="bg-slate-700/50 border-slate-600 text-white"
                required
                data-testid="merchant-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Название компании</Label>
              <Input
                value={merchantForm.company_name}
                onChange={(e) => setMerchantForm({ ...merchantForm, company_name: e.target.value })}
                placeholder="ТОО Компания"
                className="bg-slate-700/50 border-slate-600 text-white"
                data-testid="merchant-company-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">БИН</Label>
              <Input
                value={merchantForm.bin}
                onChange={(e) => setMerchantForm({ ...merchantForm, bin: e.target.value })}
                placeholder="123456789012"
                className="bg-slate-700/50 border-slate-600 text-white font-mono"
                maxLength={20}
                data-testid="merchant-bin-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Telegram</Label>
              <Input
                value={merchantForm.telegram}
                onChange={(e) => setMerchantForm({ ...merchantForm, telegram: e.target.value })}
                placeholder="@username или +7..."
                className="bg-slate-700/50 border-slate-600 text-white"
                data-testid="merchant-telegram-input"
              />
            </div>

            {/* Cabinet credentials section */}
            <div className="pt-4 border-t border-slate-700">
              <p className="text-sm font-medium text-slate-300 mb-3">Вход в личный кабинет</p>
              
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-slate-400 text-sm">Логин</Label>
                  <Input
                    value={merchantForm.cabinet_login}
                    onChange={(e) => setMerchantForm({ ...merchantForm, cabinet_login: e.target.value })}
                    placeholder="merchant_login"
                    className="bg-slate-700/50 border-slate-600 text-white"
                    data-testid="merchant-cabinet-login-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-slate-400 text-sm">
                    {editingMerchant ? 'Новый пароль (оставьте пустым, чтобы не менять)' : 'Пароль'}
                  </Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={merchantForm.cabinet_password}
                      onChange={(e) => setMerchantForm({ ...merchantForm, cabinet_password: e.target.value })}
                      placeholder={editingMerchant ? '••••••••' : 'Пароль'}
                      className="bg-slate-700/50 border-slate-600 text-white pr-10"
                      data-testid="merchant-cabinet-password-input"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0 text-slate-400 hover:text-white"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowMerchantDialog(false)}
                className="border-slate-600 text-slate-300"
              >
                Отмена
              </Button>
              <Button
                type="submit"
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                disabled={savingMerchant}
                data-testid="save-merchant-btn"
              >
                {savingMerchant ? (
                  <RefreshCw className="w-4 h-4 animate-spin mr-1" />
                ) : null}
                {editingMerchant ? 'Сохранить' : 'Создать'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Credentials Dialog */}
      <Dialog open={showCredsDialog} onOpenChange={setShowCredsDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-500" />
              Учётные данные для входа
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Мерчант: {credsMerchant?.name || credsMerchant?.company_name} (#{credsMerchant?.id})
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSaveCreds} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Логин</Label>
              <Input
                value={merchantCreds.cabinet_login}
                onChange={(e) => setMerchantCreds({ ...merchantCreds, cabinet_login: e.target.value })}
                placeholder="merchant_login"
                className="bg-slate-700/50 border-slate-600 text-white"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Пароль</Label>
              <div className="relative">
                <Input
                  type={showCredsPassword ? 'text' : 'password'}
                  value={merchantCreds.cabinet_password}
                  onChange={(e) => setMerchantCreds({ ...merchantCreds, cabinet_password: e.target.value })}
                  placeholder="Пароль"
                  className="bg-slate-700/50 border-slate-600 text-white pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowCredsPassword(!showCredsPassword)}
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0 text-slate-400 hover:text-white"
                >
                  {showCredsPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <p className="text-xs text-amber-400">
                ⚠️ Эти данные используются мерчантом для входа в личный кабинет. После сохранения отправьте их мерчанту.
              </p>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCredsDialog(false)}
                className="border-slate-600 text-slate-300"
              >
                Отмена
              </Button>
              <Button
                type="submit"
                className="bg-amber-500 hover:bg-amber-600 text-white"
                disabled={savingCreds}
              >
                {savingCreds ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : null}
                Сохранить
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Payment Link Dialog */}
      <Dialog open={showCreateLink} onOpenChange={setShowCreateLink}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link className="w-5 h-5 text-emerald-500" />
              Создать платежную ссылку
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Мерчант: {selectedMerchant?.name} (#{selectedMerchant?.id})
            </DialogDescription>
          </DialogHeader>

          {!createdLink ? (
            <form onSubmit={handleCreateLink} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Сумма</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={linkForm.amount}
                    onChange={(e) => setLinkForm({ ...linkForm, amount: e.target.value })}
                    placeholder="1000"
                    className="bg-slate-700/50 border-slate-600 text-white"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Валюта</Label>
                  <Select
                    value={linkForm.currency}
                    onValueChange={(value) => setLinkForm({ ...linkForm, currency: value })}
                  >
                    <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="KZT">KZT (тенге)</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                      <SelectItem value="RUB">RUB</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Описание (опционально)</Label>
                <Input
                  value={linkForm.description}
                  onChange={(e) => setLinkForm({ ...linkForm, description: e.target.value })}
                  placeholder="Оплата заказа"
                  className="bg-slate-700/50 border-slate-600 text-white"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Номер заказа (опционально)</Label>
                <Input
                  value={linkForm.order_number}
                  onChange={(e) => setLinkForm({ ...linkForm, order_number: e.target.value })}
                  placeholder="ORD-12345"
                  className="bg-slate-700/50 border-slate-600 text-white"
                />
              </div>

              <DialogFooter className="mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateLink(false)}
                  className="border-slate-600 text-slate-300"
                >
                  Отмена
                </Button>
                <Button
                  type="submit"
                  className="bg-emerald-500 hover:bg-emerald-600 text-white"
                >
                  Создать ссылку
                </Button>
              </DialogFooter>
            </form>
          ) : (
            <div className="space-y-4 mt-4">
              <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                <p className="text-sm text-emerald-400 mb-2">Ссылка создана!</p>
                <p className="text-lg font-bold text-white">
                  {formatAmount(createdLink.amount, createdLink.currency)}
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Ссылка для оплаты</Label>
                <div className="flex gap-2">
                  <Input
                    value={createdLink.payment_url || createdLink.payment_link}
                    readOnly
                    className="bg-slate-700/50 border-slate-600 text-white font-mono text-sm"
                  />
                  <Button
                    onClick={() => copyToClipboard(createdLink.payment_url || createdLink.payment_link)}
                    variant="outline"
                    className="border-slate-600 text-slate-300 shrink-0"
                  >
                    {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              <div className="text-sm text-slate-400">
                <p>Токен: {createdLink.token}</p>
              </div>

              <DialogFooter>
                <Button
                  onClick={() => setShowCreateLink(false)}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white w-full"
                >
                  Закрыть
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MerchantsPage;
